import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS } from '../platform/constants';
import { LAST_READ_MODULES } from '../platform/lastRead/lastReadModules';
import { markItemAsRead } from '../platform/lastRead/lastReadService';
import {
  isPinnedActive,
  parsePinnedUntil,
  sortPinnedItems,
} from '../platform/pinnedContent';
import { syncAttachmentFields } from '../platform/contentAttachments';
import {
  deleteContentAttachmentStorage,
  uploadFilesAsAttachments,
} from './attachmentUploadService';
import { withTenantScope } from './contentWriteFields';
import { uploadFileToStorage } from './storageUpload';
import {
  applyTenantScopeToCollection,
  buildCreatedAtListQuery,
  buildOrderedListQuery,
  canViewAllUnitsForTenant,
  mapAndFilterSnapshot,
} from './tenantListQuery';
import { getVisibleUnitIds } from './contentQueryHelpers';

export const NOTICES_PAGE_SIZE = 20;
export const PINNED_NOTICES_LIMIT = 5;

function noticesCollection() {
  return collection(db, COLLECTIONS.NOTICES);
}

function mapNotice(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? null,
    pinnedUntil: parsePinnedUntil(data.pinnedUntil),
    pinPriority: data.pinPriority ?? 0,
  };
}

function isNoticeVisibleToUser(user, memberships, tenantId, notice) {
  if (!user) return false;
  if (canViewAllUnitsForTenant(user, memberships, tenantId)) {
    return true;
  }
  const visibleUnits = getVisibleUnitIds(user);
  return visibleUnits.includes(notice.unit);
}

function buildNoticesListQuery(user, {
  pageSize = NOTICES_PAGE_SIZE,
  lastVisible = null,
  tenantId,
  memberships = [],
} = {}) {
  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.NOTICES,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
}

export function buildPinnedNoticesQuery(tenantId) {
  const scoped = applyTenantScopeToCollection(COLLECTIONS.NOTICES, tenantId);
  return query(
    scoped,
    where('pinnedUntil', '>', Timestamp.now()),
    orderBy('pinnedUntil', 'asc'),
    limit(PINNED_NOTICES_LIMIT),
  );
}

export function buildNoticesPageQuery(user, tenantId, memberships, options = {}) {
  return buildNoticesListQuery(user, {
    tenantId,
    memberships,
    pageSize: options.pageSize ?? NOTICES_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export async function fetchActivePinnedNotices(user, tenantId, memberships) {
  if (!tenantId) {
    return [];
  }

  const pinnedQuery = buildPinnedNoticesQuery(tenantId);
  const querySnapshot = await getDocs(pinnedQuery);
  const pinned = mapAndFilterSnapshot(querySnapshot, tenantId, mapNotice)
    .filter((notice) => isPinnedActive(notice))
    .filter((notice) => isNoticeVisibleToUser(user, memberships, tenantId, notice));

  return sortPinnedItems(pinned);
}

export async function countActivePinnedNotices(tenantId, excludeNoticeId = null) {
  if (!tenantId) {
    return 0;
  }

  const pinnedQuery = buildPinnedNoticesQuery(tenantId);
  const querySnapshot = await getDocs(pinnedQuery);
  return querySnapshot.docs
    .map(mapNotice)
    .filter((notice) => isPinnedActive(notice))
    .filter((notice) => notice.id !== excludeNoticeId)
    .length;
}

export async function fetchNoticesPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildNoticesPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    notices: mapAndFilterSnapshot(querySnapshot, tenantId, mapNotice),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export async function getUserNoticeLastRead(userId) {
  const userDocSnap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
  return userDocSnap.data()?.lastRead?.notices || {};
}

export async function fetchMaxNoticeOrder(tenantId) {
  if (!tenantId) {
    return 0;
  }

  const maxOrderQuery = buildOrderedListQuery({
    collectionName: COLLECTIONS.NOTICES,
    tenantId,
    user: null,
    memberships: [],
    orderField: 'order',
    orderDirection: 'desc',
    pageSize: 1,
  });
  const snapshot = await getDocs(maxOrderQuery);

  if (snapshot.empty) {
    return 0;
  }

  return snapshot.docs[0].data().order || 0;
}

export async function uploadNoticeFiles(files, onProgress, tenantId) {
  return uploadFilesAsAttachments(files, 'notices', { onProgress, tenantId });
}

export async function uploadNoticeThumbnail(thumbnail, onProgress, tenantId) {
  return uploadFileToStorage(thumbnail, 'notice-thumbnails', { onProgress, tenantId });
}

export async function createNotice(user, noticeData, activeTenantId) {
  const attachmentFields = noticeData.attachments
    ? syncAttachmentFields(noticeData.attachments)
    : syncAttachmentFields(noticeData.fileUrl ? [{ url: noticeData.fileUrl }] : []);

  const pinFields = {};
  if (noticeData.pinnedUntil) {
    pinFields.pinnedUntil = noticeData.pinnedUntil instanceof Date
      ? Timestamp.fromDate(noticeData.pinnedUntil)
      : noticeData.pinnedUntil;
    pinFields.pinPriority = noticeData.pinPriority ?? 0;
  } else {
    pinFields.pinnedUntil = null;
    pinFields.pinPriority = 0;
  }

  await addDoc(
    noticesCollection(),
    withTenantScope(activeTenantId, {
      ...noticeData,
      ...attachmentFields,
      ...pinFields,
      createdAt: noticeData.createdAt ?? serverTimestamp(),
    }),
  );
}

export async function updateNotice(noticeId, noticeData) {
  const payload = { ...noticeData };
  if (noticeData.attachments) {
    Object.assign(payload, syncAttachmentFields(noticeData.attachments));
  }

  if ('pinnedUntil' in noticeData) {
    payload.pinnedUntil = noticeData.pinnedUntil
      ? (noticeData.pinnedUntil instanceof Date
        ? Timestamp.fromDate(noticeData.pinnedUntil)
        : noticeData.pinnedUntil)
      : null;
    payload.pinPriority = noticeData.pinnedUntil ? (noticeData.pinPriority ?? 0) : 0;
  }

  await updateDoc(doc(db, COLLECTIONS.NOTICES, noticeId), payload);
}

export async function deleteNotice(noticeId) {
  const noticeRef = doc(db, COLLECTIONS.NOTICES, noticeId);
  const noticeSnap = await getDoc(noticeRef);
  if (noticeSnap.exists()) {
    await deleteContentAttachmentStorage(noticeSnap.data(), {
      extraUrls: [noticeSnap.data()?.thumbnailUrl],
    });
  }
  await deleteDoc(noticeRef);
}

export async function updateNoticeOrder(noticeId, order) {
  await updateDoc(doc(db, COLLECTIONS.NOTICES, noticeId), { order });
}

export async function markNoticeAsRead(userId, noticeId) {
  await markItemAsRead(userId, LAST_READ_MODULES.NOTICES, noticeId);
}

export { mapNotice };
